import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  UsersRound,
} from "lucide-react";

import { getCalendar } from "./schedule-api";
import {
  calendarWindow,
  formatRangeLabel,
  shiftAnchor,
  todayKey,
  type ScheduleView,
} from "./schedule-date";
import { DayView, EventDetail, MonthView, TeamView, WeekView } from "./ScheduleViews";
import type { CalendarEvent, CalendarOverview } from "./types";
import { useVisiblePolling } from "./use-visible-polling";

const empty: CalendarOverview = { events: [], teams: [] };
const views: Array<[ScheduleView, string]> = [
  ["day", "Day"],
  ["week", "Week"],
  ["month", "Month"],
  ["team", "By team"],
];

export function SchedulePage() {
  const [anchor, setAnchor] = useState(todayKey);
  const [view, setView] = useState<ScheduleView>("week");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [overview, setOverview] = useState(empty);
  const [selected, setSelected] = useState<CalendarEvent>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const window = useMemo(() => calendarWindow(anchor, view), [anchor, view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCalendar(
        window.start,
        window.end,
        view === "team" && selectedTeamId ? selectedTeamId : undefined,
      );
      setOverview(data);
      setFailed(false);
      if (view === "team" && !selectedTeamId)
        setSelectedTeamId(data.teams.find((team) => team.active)?.id ?? data.teams[0]?.id ?? "");
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [selectedTeamId, view, window.end, window.start]);

  useEffect(() => void load(), [load]);
  useVisiblePolling(load, 30_000);

  const appointments = overview.events.filter((event) => event.kind === "APPOINTMENT").length;
  const blocked = overview.events.length - appointments;
  const unassigned = overview.events.filter(
    (event) => event.kind === "APPOINTMENT" && !event.teamId,
  ).length;
  return (
    <div className="page-content schedule-page">
      <header className="page-intro client-intro">
        <div>
          <span className="eyebrow lime">Operational schedule</span>
          <h1>Every visit, placed with care.</h1>
          <p>
            See customer work and protected time in one Massachusetts-based calendar, without
            risking an unchecked schedule change.
          </p>
        </div>
        <div className="schedule-assurance">
          <ShieldCheck />
          <span>
            <strong>Conflict-safe foundation</strong>
            <small>Writes remain guarded</small>
          </span>
        </div>
      </header>

      <section className="schedule-summary" aria-label="Schedule summary">
        <Summary label="Appointments" value={appointments} icon={CalendarDays} />
        <Summary label="Protected windows" value={blocked} icon={ShieldCheck} />
        <Summary label="Unassigned work" value={unassigned} icon={UsersRound} />
        <Summary
          label="Teams"
          value={overview.teams.filter((team) => team.active).length}
          icon={UsersRound}
        />
      </section>

      <section className="content-card calendar-card">
        <div className="calendar-toolbar">
          <div className="calendar-navigation">
            <button className="secondary-button today-button" onClick={() => setAnchor(todayKey())}>
              Today
            </button>
            <button
              className="icon-button bordered"
              onClick={() => setAnchor((value) => shiftAnchor(value, view, -1))}
              aria-label="Previous period"
            >
              <ChevronLeft />
            </button>
            <button
              className="icon-button bordered"
              onClick={() => setAnchor((value) => shiftAnchor(value, view, 1))}
              aria-label="Next period"
            >
              <ChevronRight />
            </button>
            <h2>{formatRangeLabel(anchor, view)}</h2>
          </div>
          <div className="calendar-actions">
            {view === "team" ? (
              <select
                aria-label="Team"
                value={selectedTeamId}
                onChange={(event) => setSelectedTeamId(event.target.value)}
              >
                <option value="">Choose a team</option>
                {overview.teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                    {team.active ? "" : " · inactive"}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              className="icon-button bordered"
              onClick={() => void load()}
              aria-label="Refresh schedule"
            >
              <RefreshCw className={loading ? "spin" : ""} />
            </button>
            <div className="calendar-view-switch" role="tablist" aria-label="Calendar view">
              {views.map(([key, label]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={view === key}
                  className={view === key ? "active" : ""}
                  onClick={() => setView(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {failed ? (
          <div className="load-error" role="alert">
            We couldn’t refresh the operational schedule.
            <button onClick={() => void load()}>Try again</button>
          </div>
        ) : null}
        {loading && overview.events.length === 0 ? (
          <div className="table-state calendar-loading">
            <LoaderCircle className="spin" /> Loading schedule…
          </div>
        ) : null}
        {!loading || overview.events.length ? (
          <CalendarBody
            view={view}
            anchor={anchor}
            dates={window.dates}
            overview={overview}
            selectedTeamId={selectedTeamId}
            onOpen={setSelected}
          />
        ) : null}
        <footer className="calendar-legend">
          <span>
            <i className="appointment-dot" /> Appointment
          </span>
          <span>
            <i className="time-off-dot" /> Time off
          </span>
          <span>
            <i className="block-dot" /> Schedule block
          </span>
          <small>Times shown in Massachusetts local time</small>
        </footer>
      </section>
      {selected ? <EventDetail event={selected} onClose={() => setSelected(undefined)} /> : null}
    </div>
  );
}

function CalendarBody({
  view,
  anchor,
  dates,
  overview,
  selectedTeamId,
  onOpen,
}: {
  view: ScheduleView;
  anchor: string;
  dates: string[];
  overview: CalendarOverview;
  selectedTeamId: string;
  onOpen: (event: CalendarEvent) => void;
}) {
  if (view === "day") return <DayView date={anchor} events={overview.events} onOpen={onOpen} />;
  if (view === "week") return <WeekView dates={dates} events={overview.events} onOpen={onOpen} />;
  if (view === "month")
    return <MonthView dates={dates} anchor={anchor} events={overview.events} onOpen={onOpen} />;
  return (
    <TeamView
      date={anchor}
      events={overview.events}
      teams={overview.teams}
      selectedTeamId={selectedTeamId}
      onOpen={onOpen}
    />
  );
}

function Summary({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof CalendarDays;
}) {
  return (
    <article>
      <span>
        <Icon /> {label}
      </span>
      <strong>{value}</strong>
    </article>
  );
}
